import React, { useEffect, useState, useRef } from "react";
import { fetchUserDetails } from "../utils/adminUserData";
import { useAuth } from "../utils/adminContext";
import { formatDateDDMMMYYYY, timeAgo } from "../utils/dateHelpers";
import { LuChevronDown, LuChevronUp } from "react-icons/lu";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import getPageViews from "../utils/pageViewApi";
import { FaArrowLeft } from "react-icons/fa6";
import { IoCloseOutline } from "react-icons/io5";

const AdminUserDetails = () => {
  // Existing states for user table and filters
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [sortedUsers, setSortedUsers] = useState([]);
  const [visibleCount, setVisibleCount] = useState(25);
  const [visibleUsers, setVisibleUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isLoggedIn, openSignInModal } = useAuth();

  // Date range state for date picker
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Existing state for login popup and refs for click outside handling
  const [openLoginPopupIndex, setOpenLoginPopupIndex] = useState(null);
  const popupRef = useRef(null);
  const dropdownRef = useRef(null);

  // ------ NEW STATES FOR PAGE VIEWS, MODAL & SELECTED USER ------
  const [isPageViewVisible, setIsPageViewVisible] = useState(false);
  const [pageViews, setPageViews] = useState([]);
  const [selectedSessionData, setSelectedSessionData] = useState(null);
  // eslint-disable-next-line
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Close login popup when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setOpenLoginPopupIndex(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close category dropdown when clicking outside
  useEffect(() => {
    function handleClickOutsideDropdown(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutsideDropdown);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
  }, []);

  // Open sign-in modal if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      openSignInModal();
    }
  }, [isLoggedIn, openSignInModal]);

  // Fetch user data when logged in
  useEffect(() => {
    const getUsers = async () => {
      if (isLoggedIn) {
        setLoading(true);
        try {
          const userData = await fetchUserDetails();
          setUsers(userData);
          setFilteredUsers(userData);
        } catch (error) {
          console.error("Error fetching users:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setUsers([]);
        setFilteredUsers([]);
      }
    };
    getUsers();
  }, [isLoggedIn]);

  // Define category filter options (added "Date Range")
  const categoryOptions = [
    { key: "phoneNo", label: "Phone Number" },
    { key: "email", label: "Email" },
    { key: "organizationName", label: "Organization" },
    { key: "address", label: "Location" },
    { key: "date", label: "Date Range" },
  ];

  // Toggle checkbox selection for a category
  const handleCheckboxChange = (key) => {
    setSelectedCategories((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  // Filter users based on search term, selected categories, and date range
  useEffect(() => {
    let filtered = [...users];

    // Text search filter for non-date categories
    if (searchTerm) {
      const textCategories =
        selectedCategories.length > 0
          ? selectedCategories.filter((cat) => cat !== "date")
          : ["phoneNo", "email", "organizationName", "address"];
      if (textCategories.length > 0) {
        filtered = filtered.filter((user) =>
          textCategories.some((category) => {
            const value = user[category]
              ? user[category].toString().toLowerCase()
              : "";
            return value.includes(searchTerm.toLowerCase());
          })
        );
      }
    }

    // Date range filter if "Date Range" is selected
    if (selectedCategories.includes("date") && startDate && endDate) {
      filtered = filtered.filter((user) => {
        const lastLogin = new Date(user.lastLoginDate);
        return lastLogin >= startDate && lastLogin <= endDate;
      });
    }

    setFilteredUsers(filtered);
    setVisibleCount(25); // Reset pagination when filters change
  }, [users, searchTerm, selectedCategories, startDate, endDate]);

  // Sort filtered users (latest login on top)
  useEffect(() => {
    const sorted = [...filteredUsers].sort(
      (a, b) => new Date(b.lastLoginDate) - new Date(a.lastLoginDate)
    );
    setSortedUsers(sorted);
  }, [filteredUsers]);

  // Update visible users based on pagination
  useEffect(() => {
    setVisibleUsers(sortedUsers.slice(0, visibleCount));
  }, [sortedUsers, visibleCount]);

  // Infinite scroll for user table
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollTop + clientHeight >= scrollHeight - 5 &&
      visibleCount < sortedUsers.length
    ) {
      setVisibleCount((prev) => prev + 25);
    }
  };

  // ------ HANDLER TO FETCH AND SHOW PAGE VIEWS FOR SELECTED USER ------
  const handleLoginCountClick = async (userId) => {
    try {
      // Save the selected user id
      setSelectedUserId(userId);
      const data = await getPageViews();
      // Convert the integer userId to string for comparison
      const filteredPageViews = data.filter(
        (pv) => pv.userId === userId.toString()
      );
      setPageViews(filteredPageViews);
      setIsPageViewVisible(true);
    } catch (error) {
      console.error("Error fetching page views:", error);
    }
  };

  // ------ HANDLER TO OPEN THE SESSION TIMELINE MODAL ------
  const handleSessionIdClick = (sessionData) => {
    // Only open modal if a valid session id exists
    if (sessionData.sessionId && sessionData.userId) {
      setSelectedSessionData(sessionData);
    }
  };

  // ------ Close Modal ------
  const handleCloseModal = () => {
    setSelectedSessionData(null);
  };

  if (!isLoggedIn) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="spin-loader"></div>
      </div>
    );
  }

  return (
    <>
      <div className="flex md:flex-row flex-col md:space-x-4 space-y-3 md:space-y-0 lg:px-8 px-5 mb-6">
        {/* Category Filter Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
            className="min-w-[240px] border text-[13px] md:text-[15px] flex justify-between items-center px-3 border-gray-200 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {selectedCategories.length > 0
              ? selectedCategories
                  .map((cat) => {
                    const found = categoryOptions.find(
                      (option) => option.key === cat
                    );
                    return found ? found.label : cat;
                  })
                  .join(", ")
              : "Filter by Category"}
            {isCategoryDropdownOpen ? (
              <LuChevronDown className="ml-2" />
            ) : (
              <LuChevronUp className="ml-2" />
            )}
          </button>
          {isCategoryDropdownOpen && (
            <div className="absolute z-10 mt-2 w-[240px] p-4 space-y-3 bg-white border border-gray-200 rounded-md shadow-lg">
              {categoryOptions.map((option) => (
                <div key={option.key} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(option.key)}
                    onChange={() => handleCheckboxChange(option.key)}
                    className="mr-2 border-gray-300 w-5 h-5 rounded-md"
                  />
                  <label>{option.label}</label>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Search Box */}
        <input
          type="text"
          placeholder="Search by Category..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-[240px] border text-[15px] flex justify-center items-center px-3 border-gray-200 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />

        {/* Date Range Display Box (only if "Date Range" is selected) */}
        {selectedCategories.includes("date") && (
          <div className="relative">
            <button
              onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
              className="min-w-[240px] border text-[15px] flex justify-between items-center px-3 border-gray-200 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {startDate && endDate
                ? `${formatDateDDMMMYYYY(startDate)} - ${formatDateDDMMMYYYY(
                    endDate
                  )}`
                : "Select Date Range"}
              {isDatePickerOpen ? (
                <LuChevronUp className="ml-2" />
              ) : (
                <LuChevronDown className="ml-2" />
              )}
            </button>
            {isDatePickerOpen && (
              <div className="absolute mt-2 z-10">
                <ReactDatePicker
                  selected={startDate}
                  onChange={(dates) => {
                    const [start, end] = dates;
                    setStartDate(start);
                    setEndDate(end);
                  }}
                  startDate={startDate}
                  endDate={endDate}
                  selectsRange
                  inline
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Conditional Rendering: Show Page Views or User Table */}
      {isPageViewVisible ? (
        <>
          {/* Back Button to return to User Table */}
          <div className="mb-4 lg:px-8 px-5">
            <button
              onClick={() => setIsPageViewVisible(false)}
              className="px-2 py-1.5 bg-blue-500 text-white rounded"
            >
              <FaArrowLeft />
            </button>
          </div>

          {/* Page Views Table */}
          <div className="overflow-y-auto md:max-h-[calc(100vh-290px)] max-h-[calc(100vh-265px)] scroll-bar">
            <table className="min-w-full text-sm whitespace-nowrap">
              <thead className="bg-[#EFF4FF] text-[#929EB6] border-b border-[#D1DFFC] sticky top-0 z-[2]">
                <tr>
                  <th className="p-4 text-left font-medium">S.No</th>
                  <th className="p-4 text-left font-medium">User Name</th>
                  <th className="p-4 text-left font-medium">Email</th>
                  <th className="p-4 text-left font-medium">Page Count</th>
                </tr>
              </thead>
              <tbody>
                {pageViews.length > 0 ? (
                  pageViews.map((pv, index) => (
                    <tr
                      key={index}
                      className={`${
                        index % 2 === 0 ? "bg-[#F4F7FF]" : "bg-[#EBF1FF]"
                      } border-b border-[#DCE2EC]`}
                    >
                      <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs">
                        {index + 1}
                      </td>
                      <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs">
                        {pv.userName}
                      </td>
                      <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs">
                        {pv.email && pv.userId ? (
                          <span className="text-[#193B68] xl:text-sm lg:text-xs">
                            {pv.email}
                          </span>
                        ) : (
                          <span>{pv.email || "-"}</span>
                        )}
                      </td>
                      <td className="p-4">
                        {pv.pages && pv.userId ? (
                          <span
                            onClick={() => handleSessionIdClick(pv)}
                            className="cursor-pointer font-medium text-blue-500"
                          >
                            {pv.pages ? pv.pages.length : 0}
                          </span>
                        ) : (
                          <span>{pv.pages || "-"}</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      className="py-5 px-4 bg-[#F4F7FF]/80 text-center text-gray-500"
                    >
                      No page views found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        // ----- ORIGINAL USER TABLE -----
        <div
          className="overflow-y-auto md:max-h-[calc(100vh-245px)] max-h-[calc(100vh-265px)] scroll-bar"
          onScroll={handleScroll}
        >
          <table className="min-w-full text-sm whitespace-nowrap">
            <thead className="bg-[#EFF4FF] text-[#929EB6] border-b border-[#D1DFFC] sticky top-0 z-[2]">
              <tr>
                <th className="p-4 text-left font-medium">S.No</th>
                <th className="p-4 text-left font-medium">User Name</th>
                <th className="p-4 text-left font-medium">Phone No</th>
                <th className="p-4 text-left font-medium">Email</th>
                <th className="p-4 text-left font-medium">Organization</th>
                <th className="p-4 text-left font-medium">Location</th>
                <th className="p-4 text-left font-medium">Login Page</th>
                <th className="p-4 text-left font-medium">Last Login</th>
                <th className="p-4 text-left font-medium">No of Session</th>
                <th className="p-4 text-left font-medium">Member Since</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.length > 0 ? (
                visibleUsers.map((user, index) => (
                  <tr
                    key={index}
                    className={`${
                      index % 2 === 0 ? "bg-[#F4F7FF]" : "bg-[#EBF1FF]"
                    } border-b border-[#DCE2EC]`}
                  >
                    <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs">
                      {index + 1}
                    </td>
                    <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs">
                      {user.userName}
                    </td>
                    <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs">
                      {user.phoneNo || "-"}
                    </td>
                    <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs">
                      {user.email}
                    </td>
                    <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs">
                      {user.organizationName || "-"}
                    </td>
                    <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs">
                      {user.address || "-"}
                    </td>
                    <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs relative">
                      {(() => {
                        const pages = user.loginPage
                          ? user.loginPage
                              .split(",")
                              .map((p) => p.trim())
                              .filter((p) => p && p !== "undefined")
                          : [];
                        if (pages.length === 0) {
                          return <span>-</span>;
                        }
                        const latestPage = pages[pages.length - 1];
                        const extraPages = pages.slice(0, pages.length - 1);
                        return (
                          <>
                            <span>{latestPage}</span>
                            {extraPages.length > 0 && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenLoginPopupIndex(
                                      openLoginPopupIndex === index
                                        ? null
                                        : index
                                    );
                                  }}
                                  className="ml-2 text-blue-500 underline focus:outline-none"
                                >
                                  +{extraPages.length}
                                </button>
                                {openLoginPopupIndex === index && (
                                  <div
                                    ref={popupRef}
                                    className="absolute top-full w-80 mt-2 bg-white border border-gray-200 rounded shadow-lg z-[1]"
                                  >
                                    <ul className="max-h-[250px] overflow-auto">
                                      {extraPages.map((page, idx) => (
                                        <li
                                          key={idx}
                                          className="p-2 hover:bg-gray-100 border-b last:border-b-0"
                                        >
                                          {page}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs">
                      {formatDateDDMMMYYYY(user.lastLoginDate) || "-"}
                    </td>
                    <td
                      onClick={() => handleLoginCountClick(user.userId)}
                      className="cursor-pointer p-4 text-blue-500 xl:text-sm lg:text-xs"
                    >
                      {user.loginCount || "-"}
                    </td>
                    <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs">
                      {timeAgo(user.registerDate) || "-"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="10"
                    className="py-5 px-4 bg-[#F4F7FF]/80 text-center text-gray-500"
                  >
                    No users found matching the filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ------ MODAL FOR SESSION TIMELINE ------ */}
      {selectedSessionData && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black opacity-50"
            onClick={handleCloseModal}
          ></div>
          <div className="bg-white rounded-xl mx-3 md:mx-0 shadow-lg z-10 w-full md:max-w-4xl">
            <div className="flex justify-between border-b items-center md:px-6 px-4 py-4">
              <h2 className="text-xl font-semibold">Session Timeline</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <IoCloseOutline className="text-2xl font-medium" />
              </button>
            </div>
            <div className="max-h-[500px] overflow-y-auto p-8 md:p-10">
              <ul className="relative border-l-2 ml-20 border-gray-300">
                {selectedSessionData.pages &&
                  (() => {
                    // Sort pages by the time they were viewed in ascending order.
                    const sortedPages = [...selectedSessionData.pages].sort(
                      (a, b) =>
                        new Date(a.pageViewedAt) - new Date(b.pageViewedAt)
                    );
                    return sortedPages.map((page, idx) => {
                      let duration = "N/A";
                      // Calculate time spent if there is a next page in the list.
                      if (idx < sortedPages.length - 1) {
                        const nextPage = sortedPages[idx + 1];
                        const diffMs =
                          new Date(nextPage.pageViewedAt) -
                          new Date(page.pageViewedAt);
                        const diffSeconds = Math.floor(diffMs / 1000);
                        duration = `${diffSeconds} sec`;
                      }
                      return (
                        <li key={idx} className="mb-4">
                          <p className="text-[10px] font-semibold absolute mt-[-3px] left-[-95px] bg-green-100 py-1 px-1.5 rounded-md text-green-600">
                            {formatDateDDMMMYYYY(new Date(page.pageViewedAt))}
                          </p>
                          <div className="absolute -left-1.5 w-3 h-3 bg-indigo-500 rounded-full border border-white"></div>
                          <div className="px-5">
                            <p className="font-medium text-xs md:text-sm mb-1">{page.pageUrl}</p>
                            <p className="md:text-[11px] text-[10px] bg-purple-100 inline-block px-1.5 rounded-md py-[3px] text-purple-500">
                              Time Spent - {duration}
                            </p>
                          </div>
                        </li>
                      );
                    });
                  })()}
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminUserDetails;
